import { create } from "zustand";
import api from "../../utils/api";
import { useCompaniesStore } from "../../store/admin/companiesStore";

export interface CompanyDemoFlags {
  centinela_demo: boolean;
  mocks_report: boolean;
}

const NO_FLAGS: CompanyDemoFlags = {
  centinela_demo: false,
  mocks_report: false,
};

interface DemoFlagsState {
  companyId: string | null;
  flags: CompanyDemoFlags;
}

const useDemoFlagsStore = create<DemoFlagsState>(() => ({
  companyId: null,
  flags: NO_FLAGS,
}));

const toFlags = (company: {
  centinela_demo?: boolean;
  mocks_report?: boolean;
} | null | undefined): CompanyDemoFlags => ({
  centinela_demo: company?.centinela_demo === true,
  mocks_report: company?.mocks_report === true,
});

const activeCompanyId = (): string | null => {
  const { companyId, companyUser } = useCompaniesStore.getState();
  return companyId || companyUser || null;
};

const knownCompany = (id: string) => {
  const { allCompanies, companies } = useCompaniesStore.getState();
  return (
    allCompanies.find((company) => company.id === id) ??
    companies.find((company) => company.id === id) ??
    null
  );
};

const apply = (companyId: string | null, flags: CompanyDemoFlags) => {
  const current = useDemoFlagsStore.getState();
  const unchanged =
    current.companyId === companyId &&
    current.flags.centinela_demo === flags.centinela_demo &&
    current.flags.mocks_report === flags.mocks_report;
  unchanged || useDemoFlagsStore.setState({ companyId, flags });
};

const fetchedFlags = new Map<string, CompanyDemoFlags>();
let fetchingId: string | null = null;

const resolveFlags = () => {
  const id = activeCompanyId();
  if (!id) return apply(null, NO_FLAGS);

  const known = knownCompany(id);
  if (known) return apply(id, toFlags(known));

  const cached = fetchedFlags.get(id);
  if (cached) return apply(id, cached);

  apply(id, NO_FLAGS);
  if (fetchingId === id || !localStorage.getItem("token")) return;

  fetchingId = id;
  api
    .get("/company", { params: { id } })
    .then(({ data }) => fetchedFlags.set(id, toFlags(data?.data?.data)))
    .catch(() => fetchedFlags.set(id, NO_FLAGS))
    .finally(() => {
      fetchingId = null;
      resolveFlags();
    });
};

resolveFlags();
useCompaniesStore.subscribe(resolveFlags);

export const getCompanyDemoFlags = (): CompanyDemoFlags =>
  useDemoFlagsStore.getState().flags;

export const useCompanyDemoFlag = (flag: keyof CompanyDemoFlags): boolean =>
  useDemoFlagsStore((state) => state.flags[flag]);
